/**
 * Token Manager - Centralized token refresh management
 * Prevents multiple concurrent refresh token API calls
 *
 * Handles the scenario where refresh token API fails (401/error):
 * - Implements cooldown period to prevent immediate retries
 * - Automatically logs out user on 401 refresh token errors
 * - Prevents multiple refresh attempts when refresh API is failing
 */

import axios from 'axios'
import { logoutCyberArk, setToken } from '../Helpers/Util'

// Global token refresh state
let isGloballyRefreshingToken = false
let globalRefreshTokenPromise: Promise<any> | null = null
let lastRefreshFailureTime: number | null = null
const REFRESH_FAILURE_COOLDOWN = 2000 // 2 seconds cooldown after refresh failure

/**
 * Centralized token refresh function to prevent concurrent calls
 * @param baseUrl - The base URL for the refresh token API
 * @returns Promise that resolves when token is refreshed
 */
export const refreshTokenSafely = async (refreshUrl: string): Promise<any> => {
  // If token refresh is already in progress globally, return the existing promise
  if (isGloballyRefreshingToken && globalRefreshTokenPromise) {
    return globalRefreshTokenPromise
  }

  // Check if we're in cooldown period after a recent refresh failure
  if (lastRefreshFailureTime && Date.now() - lastRefreshFailureTime < REFRESH_FAILURE_COOLDOWN) {
    // Don't attempt refresh if we recently failed, throw error to prevent multiple attempts
    const timeSinceFailure = Date.now() - lastRefreshFailureTime
    console.warn(
      `Token refresh attempted too soon after failure (${timeSinceFailure}ms ago). Cooldown: ${REFRESH_FAILURE_COOLDOWN}ms`,
    )
    throw new Error('Token refresh failed recently, please login again')
  }

  // Mark that token refresh is in progress globally
  isGloballyRefreshingToken = true

  globalRefreshTokenPromise = axios({
    url: refreshUrl,
    method: 'get',
    data: {},
  })
    .then((result) => {
      // Success - clear any previous failure time
      lastRefreshFailureTime = null

      if (result?.data?.token) {
        setToken(result?.data?.token)
      }
      return result
    })
    .catch((error) => {
      // Mark the failure time to prevent immediate retries
      lastRefreshFailureTime = Date.now()

      // For 401 errors on refresh token, don't retry - user needs to login again
      if (error?.response?.status === 401) {
        console.error('Refresh token expired or invalid - user needs to login again')
        logoutCyberArk('Session expired. Please login again.')
        throw new Error('Refresh token expired')
      }

      // Re-throw the error so calling code can handle it appropriately
      throw error
    })
    .finally(() => {
      // Reset the global state after refresh is complete (success or failure)
      isGloballyRefreshingToken = false
      globalRefreshTokenPromise = null
    })

  return globalRefreshTokenPromise
}
